// Credit Bureau PULL Information for Major US Banks
// THIS FILE SHOWS WHICH CREDIT BUREAUS BANKS PULL FROM DURING APPLICATION
// (This creates hard inquiries on your credit report)
// Based on industry research and crowdsourced data (2024)
//
// IMPORTANT DISTINCTION:
// - PULLS FROM: Which bureau(s) they check when you APPLY (creates hard inquiries) ⚠️
// - REPORTS TO: Which bureau(s) receive your account info after approval (different file)
//
// Note: Credit bureau pulls can vary by:
// - State/geographic location
// - Specific card product
// - Time period (issuers may change practices)
// - Individual applicant factors
//
// Sources: Doctor of Credit, crowdsourced data, industry reports

export const creditBureaus = {
    EXPERIAN: 'Experian',
    EQUIFAX: 'Equifax',
    TRANSUNION: 'TransUnion'
  };
  
  // Mapping of banks to credit bureaus they PULL FROM during application
  export const bankCreditBureauReporting = {
    // Tier 1 Banks - National Premium
    'Chase Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian (varies by state)',
      notes: 'Typically pulls Experian, but may vary by state and card product. Only creates 1 hard inquiry (not all 3).',
      frequency: 'One-time during application'
    },
    'American Express': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian',
      notes: 'Primarily pulls Experian. After first card approval, additional cards use soft pulls. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Bank of America': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian (sometimes TransUnion/Equifax)',
      notes: 'Generally pulls Experian, but may pull TransUnion or Equifax based on state. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Wells Fargo': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian (47%), TransUnion (37%), or Equifax',
      notes: 'Uses Experian about 47% of the time, followed by TransUnion (37%) and Equifax. Varies by state. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'US Bank': {
      bureaus: [creditBureaus.TRANSUNION],
      reportingType: 'TransUnion (sometimes Experian/Equifax)',
      notes: 'Primarily pulls TransUnion (over 50% of cases), but may use Experian or Equifax. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Citibank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian (sometimes Equifax)',
      notes: 'Typically pulls Experian, sometimes pulls Equifax or both. Creates 1-2 hard inquiries.',
      frequency: 'One-time during application'
    },
    'Goldman Sachs/Apple Card': {
      bureaus: [creditBureaus.TRANSUNION],
      reportingType: 'TransUnion',
      notes: 'Apple Card primarily pulls TransUnion. Known for soft pull credit limit increases every 90 days. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    
    // Tier 2 Banks - Regional & Strategic
    'Capital One': {
      bureaus: [creditBureaus.EXPERIAN, creditBureaus.EQUIFAX, creditBureaus.TRANSUNION],
      reportingType: '⚠️ ALL THREE BUREAUS',
      notes: '⚠️ IMPORTANT: Capital One pulls from ALL THREE credit bureaus, creating 3 hard inquiries! This is mentioned in the knowledgebase as a reason to avoid them.',
      frequency: 'One-time during application (3 inquiries)'
    },
    'Discover': {
      bureaus: [creditBureaus.EQUIFAX],
      reportingType: 'Equifax',
      notes: 'Typically pulls Equifax. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'PNC Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. May vary by state. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Truist Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian or TransUnion',
      notes: 'Commonly pulls Experian or TransUnion. Varies by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Citizens Bank': {
      bureaus: [creditBureaus.EQUIFAX],
      reportingType: 'Equifax',
      notes: 'Primarily pulls Equifax. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Navy Federal Credit Union': {
      bureaus: [creditBureaus.EQUIFAX, creditBureaus.TRANSUNION],
      reportingType: 'Equifax or TransUnion',
      notes: 'Commonly pulls Equifax for personal apps, but may also pull TransUnion. Military members only. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'PenFed Credit Union': {
      bureaus: [creditBureaus.EQUIFAX],
      reportingType: 'Equifax (rarely Experian)',
      notes: 'Primarily pulls Equifax for credit cards (most or all of the time). May use Experian for other products. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'TD Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian',
      notes: 'Typically pulls Experian. WARNING: Business cards may report to personal credit - avoid for business. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Regions Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Fifth Third Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'KeyBank': {
      bureaus: [creditBureaus.EQUIFAX],
      reportingType: 'Equifax',
      notes: 'Typically pulls Equifax. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'M&T Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Huntington Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'BB&T': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Now part of Truist. Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'SunTrust': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Now part of Truist. Generally pulls Experian. May vary by region. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Ally Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'USAA': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Experian',
      notes: 'Typically pulls Experian. Military members and families only. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'State Farm Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. NOTE: As of 2020, State Farm partnered with U.S. Bank for new cards - new applications follow U.S. Bank practices (TransUnion). Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    
    // Tier 3 Banks - Tactical Options / Subprime
    'Synchrony Bank': {
      bureaus: [creditBureaus.TRANSUNION, creditBureaus.EXPERIAN],
      reportingType: 'TransUnion or Experian',
      notes: 'Typically pulls TransUnion or Experian. Major store card issuer. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Barclays': {
      bureaus: [creditBureaus.TRANSUNION],
      reportingType: 'TransUnion',
      notes: 'Typically pulls TransUnion. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Credit One Bank': {
      bureaus: [creditBureaus.EXPERIAN, creditBureaus.EQUIFAX, creditBureaus.TRANSUNION],
      reportingType: 'Varies (all three possible)',
      notes: 'May pull from any of the three bureaus. Subprime lender with high fees. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Indigo': {
      bureaus: [creditBureaus.EXPERIAN, creditBureaus.EQUIFAX, creditBureaus.TRANSUNION],
      reportingType: 'Varies (all three possible)',
      notes: 'May pull from any of the three bureaus depending on applicant. Subprime card with high fees. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Comenity Bank': {
      bureaus: [creditBureaus.TRANSUNION, creditBureaus.EXPERIAN],
      reportingType: 'TransUnion or Experian',
      notes: 'Typically pulls TransUnion or Experian. Store card issuer. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'First Premier Bank': {
      bureaus: [creditBureaus.TRANSUNION],
      reportingType: 'Primarily TransUnion',
      notes: 'Primarily pulls TransUnion, though may use Experian or Equifax in some cases. Subprime lender with high fees. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Merrick Bank': {
      bureaus: [creditBureaus.TRANSUNION],
      reportingType: 'TransUnion',
      notes: 'Typically pulls TransUnion. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Total Visa': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime card. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Milestone': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime lender. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Surge': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime card. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Reflex': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime card. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Fingerhut': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Catalog credit account. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Aspire': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime card. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'OpenSky': {
      bureaus: [],
      reportingType: 'No Credit Pull',
      notes: 'NO CREDIT CHECK REQUIRED. Secured card that does not perform hard inquiry. Creates 0 hard inquiries.',
      frequency: 'No credit pull'
    },
    'Applied Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'First Access': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime card. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Fortiva': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Generally pulls Experian. Subprime lender. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    
    // Generic/Default entries
    'Credit Union': {
      bureaus: [creditBureaus.EXPERIAN, creditBureaus.TRANSUNION],
      reportingType: 'Varies by Credit Union',
      notes: 'Credit unions typically pull Experian or TransUnion, but this varies. Contact your specific credit union to confirm. Usually creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Regional Bank': {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian',
      notes: 'Most regional banks pull Experian, but this varies by bank and location. Contact your specific bank to confirm. Usually creates 1 hard inquiry.',
      frequency: 'One-time during application'
    },
    'Store Card': {
      bureaus: [creditBureaus.TRANSUNION, creditBureaus.EXPERIAN],
      reportingType: 'Varies by Issuer',
      notes: 'Store cards vary by issuer. Synchrony typically pulls TransUnion, Comenity pulls TransUnion or Experian, TD Bank pulls Experian. Creates 1 hard inquiry.',
      frequency: 'One-time during application'
    }
  };
  
  // Helper function to normalize bank name to translation key
  const normalizeBankNameToKey = (bankName) => {
    if (!bankName) return null;
    return bankName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/\//g, '_');
  };

  // Helper function to get credit bureau reporting info for a bank
  export const getCreditBureauReporting = (bankName) => {
    if (!bankName) return null;
    
    // Try exact match first
    if (bankCreditBureauReporting[bankName]) {
      return bankCreditBureauReporting[bankName];
    }
    
    // Try case-insensitive match
    const normalizedBankName = bankName.toLowerCase();
    const matchedKey = Object.keys(bankCreditBureauReporting).find(
      key => key.toLowerCase() === normalizedBankName
    );
    
    if (matchedKey) {
      return bankCreditBureauReporting[matchedKey];
    }
    
    // Return default if no match found
    return {
      bureaus: [creditBureaus.EXPERIAN],
      reportingType: 'Typically Experian (varies)',
      notes: 'Most banks pull Experian, but this varies by bank, location, and card product. Contact your issuer to confirm which bureau(s) they pull from. Usually creates 1 hard inquiry.',
      frequency: 'One-time during application'
    };
  };

  // Helper function to get translated credit bureau reporting info for a bank
  export const getTranslatedCreditBureauReporting = (bankName, t) => {
    if (!bankName || !t) return null;
    
    const reporting = getCreditBureauReporting(bankName);
    if (!reporting) return null;
    
    const bankKey = normalizeBankNameToKey(bankName);
    if (!bankKey) {
      // Use default translation if we can't normalize the bank name
      const translationKey = 'creditBureauReporting.default';
      return {
        ...reporting,
        reportingType: t(`${translationKey}.reportingType`, reporting.reportingType),
        notes: t(`${translationKey}.notes`, reporting.notes),
        frequency: t(`${translationKey}.frequency`, reporting.frequency)
      };
    }
    
    // Try to get translated version
    const translationKey = `creditBureauReporting.${bankKey}`;
    const translatedReportingType = t(`${translationKey}.reportingType`, reporting.reportingType);
    const translatedNotes = t(`${translationKey}.notes`, reporting.notes);
    const translatedFrequency = t(`${translationKey}.frequency`, reporting.frequency);
    
    return {
      ...reporting,
      reportingType: translatedReportingType,
      notes: translatedNotes,
      frequency: translatedFrequency
    };
  };
  
  // Helper to get bureau names as a formatted string
  export const getBureauNamesString = (bankName) => {
    const reporting = getCreditBureauReporting(bankName);
    if (!reporting || !reporting.bureaus) {
      return 'Unknown';
    }
    
    if (reporting.bureaus.length === 0) {
      return 'No Credit Pull';
    }
    
    if (reporting.bureaus.length === 3) {
      return 'All Three Bureaus';
    }
    
    return reporting.bureaus.join(', ');
  };
  
  // Helper to get bureau abbreviations
  export const getBureauAbbreviations = (bankName) => {
    const reporting = getCreditBureauReporting(bankName);
    if (!reporting || !reporting.bureaus || reporting.bureaus.length === 0) {
      return [];
    }
    
    return reporting.bureaus.map(bureau => {
      switch(bureau) {
        case creditBureaus.EXPERIAN:
          return 'EXP';
        case creditBureaus.EQUIFAX:
          return 'EQU';
        case creditBureaus.TRANSUNION:
          return 'TU';
        default:
          return bureau.substring(0, 3).toUpperCase();
      }
    });
  };
  
  export default {
    creditBureaus,
    bankCreditBureauReporting,
    getCreditBureauReporting,
    getBureauNamesString,
    getBureauAbbreviations
  };
  